import React from "react";
import { useDispatch } from "react-redux";
import axios from "axios";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";

// jaza
import {
  List,
  Filter,
  TextField,
  FunctionField,
  SelectInput,
  Datagrid,
  CreateButton,
  EditButton,
  crudDelete,
  showNotification,
  FilterButton
} from "jazasoft";

import ConfirmButton from "../../../components/ConfirmButton";
import HistoryButton from "../../../components/LibraryHistoryButton";

// helper
import hasPrivilege from "../../../utils/hasPrivilege";
import { authServerUrl } from "../../../App";
import useQuery from "../../../hooks/useQuery";
import useMutation from "../../../hooks/useMutation";
import useUnit from "../../../hooks/useUnit";
import { inputOptions, isEmpty, listFromCsv } from "../../../utils/helpers";
import { useEntityPrivilege, useSetting } from "../../../hooks";
import { CustomersFields } from "../../../utils/entityFields.js";
import SettingKeys from "../../../utils/SettingKeys.jsx";
import { ResourceId, Scope } from "../../../utils/privilege.js";

const useStyles = makeStyles(theme => ({
  buttonEdit: {
    width: theme.spacing(14)
  },
  buttonDelete: {
    width: theme.spacing(16)
  },
  buttonHistory: {
    width: theme.spacing(1)
  }
}));

const DeleteButton = ({ record, onDelete }) => {
  return (
    <ConfirmButton
      color="primary"
      variant="text"
      confirmTitle="Delete Customer?"
      disabled={record?.isReferenced}
      onConfirm={() => onDelete(record)}
    />
  );
};

const Customer = props => {
  const classes = useStyles();
  const { resource, basePath, i18nKey, roles, hasAccess } = props;
  const fields = useSetting(SettingKeys.ENTITY_FIELDS, resource);

  const { data: apis = {} } = useQuery({ url: "apis", params: { appId: "iam" } });
  const api = apis[0] || {};

  const dispatch = useDispatch();
  const [fetch] = useMutation();
  const { allUnitList } = useUnit();
  const { disableCreate, disableEdit, disableDelete } = useEntityPrivilege("customer");

  const onDelete = async record => {
    fetch(
      { url: `${resource}/${record?.id}`, method: "get" },
      {
        onSuccess: async customer => {
          if (customer.isReferenced) {
            dispatch(showNotification("Customer cannot be deleted once refernced.", { type: "danger" }));
            return;
          }
          // Delete in IAM first, then in app
          let options = {
            url: `${authServerUrl}/permissions`,
            method: "delete",
            data: {
              key: "customerId",
              name: record.name,
              value: record.id
            },
            headers: {
              "x-api-key": api?.apiKey
            }
          };
          const response = await axios.request(options); // Delete in IAM
          if (response.status === 204 || response.status === 200) {
            dispatch(crudDelete(resource, record.id, record, basePath, i18nKey)); // Delete in OptaCut
          }
        }
      }
    );
  };

  const unitChoices = [{ id: "all", name: "All Units" }, ...allUnitList.map(({ unitId, name }) => ({ id: unitId, name }))];

  return (
    <List
      actions={({ basePath, roles, hasAccess }) => (
        <div>
          {hasPrivilege(roles, hasAccess, ResourceId.CUSTOMER, Scope.WRITE) && !disableCreate && (
            <CreateButton basePath={basePath} showLabel={false} />
          )}
          {allUnitList?.length > 1 && <FilterButton showLabel={false} />}
        </div>
      )}
      filters={
        <Filter>
          <SelectInput source="unitIds" i18nKey={i18nKey} choices={unitChoices} {...inputOptions(12)} />
        </Filter>
      }
      parse={({ unitIds, ...rest }) => ({ ...rest, unitIds: unitIds === "all" ? unitIds : !isEmpty(unitIds) ? `*${unitIds}*` : undefined })}
      {...props}>
      <Datagrid className={classes.datagrid}>
        {fields.includes(CustomersFields.NAME) && <TextField source="name" />}
        {fields.includes(CustomersFields.CODE) && <TextField source="code" />}
        {allUnitList?.length > 1 && (
          <FunctionField
            source="unitIds"
            label="Units"
            render={record =>
              allUnitList
                .filter(u => listFromCsv(record?.unitIds).includes(u.unitId))
                .map(u => u.name)
                .join(", ")
            }
          />
        )}
        {fields.includes(CustomersFields.DESC) && <TextField source="desc" />}
        <HistoryButton cellClassName={classes.buttonHistory} i18nKey={props.i18nKey} />
        {hasPrivilege(roles, hasAccess, ResourceId.CUSTOMER, Scope.UPDATE) && !disableEdit && <EditButton cellClassName={classes.buttonEdit} />}
        {hasPrivilege(roles, hasAccess, ResourceId.CUSTOMER, Scope.DELETE) && !disableDelete && (
          <DeleteButton cellClassName={classes.buttonEdit} onDelete={onDelete} />
        )}
      </Datagrid>
    </List>
  );
};

export default Customer;
